import { api } from "@/lib/api";

export type ApiKeyScope =
	| "tickets:write"
	| "tickets:read"
	| "tasks:write"
	| "tasks:read"
	| "status:update";

export const ALL_SCOPES: ApiKeyScope[] = [
	"tickets:write",
	"tickets:read",
	"tasks:write",
	"tasks:read",
	"status:update",
];

export interface ApiKey {
	id: string;
	name: string;
	key_prefix: string;
	project_id: string;
	scopes: ApiKeyScope[];
	expires_at: string | null;
	last_used_at: string | null;
	last_used_ip: string | null;
	revoked_at: string | null;
	created_at: string;
	project: { id: string; name: string; key: string } | null;
}

export interface CreatedApiKey extends ApiKey {
	key: string;
}

export interface CreateApiKeyPayload {
	name: string;
	project_id: string;
	scopes: ApiKeyScope[];
	expires_at?: string | null;
}

export async function listApiKeys(): Promise<ApiKey[]> {
	const res = await api.get<{ success: boolean; data: ApiKey[] }>(
		"/api-keys",
	);
	return res.data;
}

export async function createApiKey(
	payload: CreateApiKeyPayload,
): Promise<CreatedApiKey> {
	const res = await api.post<{ success: boolean; data: CreatedApiKey }>(
		"/api-keys",
		payload,
	);
	return res.data;
}

export async function revokeApiKey(id: string): Promise<void> {
	await api.delete(`/api-keys/${id}`);
}

/**
 * Destructive: mints a new key and the old one stops working at once. A POST,
 * not a GET, so a prefetch or retry cannot rotate a live credential.
 */
export async function regenerateApiKey(
	id: string,
): Promise<{ key: string; plaintext: string } | null> {
	const res = await api.post<{
		success: boolean;
		data: { key: string; plaintext: string };
	}>(`/api-keys/${id}/regenerate`, {});
	return res.data;
}

export interface ApiKeyUsageEntry {
	id: string;
	method: string;
	path: string;
	status_code: number;
	duration_ms: number | null;
	ip: string | null;
	user_agent: string | null;
	entity_type: string | null;
	entity_id: string | null;
	error_message: string | null;
	created_at: string;
}

export interface ApiKeyUsagePage {
	data: ApiKeyUsageEntry[];
	total: number;
	page: number;
	limit: number;
}

export async function listApiKeyUsage(
	id: string,
	page = 1,
	limit = 50,
): Promise<ApiKeyUsagePage> {
	return await api.get<ApiKeyUsagePage>(
		`/api-keys/${id}/usage?page=${page}&limit=${limit}`,
	);
}
